import * as React from "react"

import type {
  ToastActionElement,
  ToastProps,
} from "@/components/ui/toast"

const TOAST_LIMIT = 3
const TOAST_REMOVE_DELAY = 3000
const TOAST_AUTO_DISMISS_DEFAULT = 2800
const TOAST_AUTO_DISMISS_DESTRUCTIVE = 6000

type ToasterToast = ToastProps & {
  id: string
  title?: React.ReactNode
  description?: React.ReactNode
  action?: ToastActionElement
}

const actionTypes = {
  ADD_TOAST: "ADD_TOAST",
  UPDATE_TOAST: "UPDATE_TOAST",
  DISMISS_TOAST: "DISMISS_TOAST",
  REMOVE_TOAST: "REMOVE_TOAST",
  NEXT_TOAST: "NEXT_TOAST",
  PREVIOUS_TOAST: "PREVIOUS_TOAST",
} as const

let count = 0

function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER
  return count.toString()
}

type ActionType = typeof actionTypes

type Action =
  | {
      type: ActionType["ADD_TOAST"]
      toast: ToasterToast
    }
  | {
      type: ActionType["UPDATE_TOAST"]
      toast: Partial<ToasterToast>
    }
  | {
      type: ActionType["DISMISS_TOAST"]
      toastId?: ToasterToast["id"]
    }
  | {
      type: ActionType["REMOVE_TOAST"]
      toastId?: ToasterToast["id"]
    }
  | {
      type: ActionType["NEXT_TOAST"]
    }
  | {
      type: ActionType["PREVIOUS_TOAST"]
    }

interface State {
  toasts: ToasterToast[]
  currentIndex: number
  isPaused: boolean
}

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>()

const addToRemoveQueue = (toastId: string) => {
  if (toastTimeouts.has(toastId)) {
    return
  }

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId)
    dispatch({
      type: "REMOVE_TOAST",
      toastId: toastId,
    })
  }, TOAST_REMOVE_DELAY)

  toastTimeouts.set(toastId, timeout)
}

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "ADD_TOAST":
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
        currentIndex: 0,
        isPaused: false,
      }

    case "UPDATE_TOAST":
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === action.toast.id ? { ...t, ...action.toast } : t
        ),
      }

    case "DISMISS_TOAST": {
      const { toastId } = action

      // ! Side effects ! - This could be extracted into a dismissToast() action,
      // but I'll keep it here for simplicity
      if (toastId) {
        addToRemoveQueue(toastId)
      } else {
        state.toasts.forEach((toast) => {
          addToRemoveQueue(toast.id)
        })
      }

      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === toastId || toastId === undefined
            ? {
                ...t,
                open: false,
              }
            : t
        ),
      }
    }
    case "REMOVE_TOAST":
      if (action.toastId === undefined) {
        return {
          ...state,
          toasts: [],
          currentIndex: 0,
        }
      }
      const newToasts = state.toasts.filter((t) => t.id !== action.toastId)
      return {
        ...state,
        toasts: newToasts,
        currentIndex: Math.min(state.currentIndex, Math.max(0, newToasts.length - 1)),
      }
    
    case "NEXT_TOAST":
      return {
        ...state,
        currentIndex: Math.min(state.currentIndex + 1, state.toasts.length - 1),
        isPaused: true,
      }
    
    case "PREVIOUS_TOAST":
      return {
        ...state,
        currentIndex: Math.max(state.currentIndex - 1, 0),
        isPaused: true,
      }
  }
}

const listeners: Array<(state: State) => void> = []

let memoryState: State = { toasts: [], currentIndex: 0, isPaused: false }

function dispatch(action: Action) {
  memoryState = reducer(memoryState, action)
  listeners.forEach((listener) => {
    listener(memoryState)
  })
}

type Toast = Omit<ToasterToast, "id">

function toast({ ...props }: Toast) {
  // Suppress empty toasts (no title and no description) — reduces noise.
  if (!props.title && !props.description) {
    return {
      id: '',
      dismiss: () => {},
      update: () => {},
    }
  }

  const id = genId()

  const update = (props: ToasterToast) =>
    dispatch({
      type: "UPDATE_TOAST",
      toast: { ...props, id },
    })
  const dismiss = () => dispatch({ type: "DISMISS_TOAST", toastId: id })

  dispatch({
    type: "ADD_TOAST",
    toast: {
      ...props,
      id,
      open: true,
      onOpenChange: (open) => {
        if (!open) dismiss()
      },
    },
  })

  // Auto-dismiss: critical toasts stay longer, regular ones disappear quickly.
  const autoDismissDelay =
    props.variant === 'destructive'
      ? TOAST_AUTO_DISMISS_DESTRUCTIVE
      : TOAST_AUTO_DISMISS_DEFAULT
  setTimeout(dismiss, autoDismissDelay)

  return {
    id: id,
    dismiss,
    update,
  }
}

function useToast() {
  const [state, setState] = React.useState<State>(memoryState)

  React.useEffect(() => {
    listeners.push(setState)
    return () => {
      const index = listeners.indexOf(setState)
      if (index > -1) {
        listeners.splice(index, 1)
      }
    }
  }, [])

  const dismissToast = React.useCallback((toastId?: string) => {
    dispatch({ type: "DISMISS_TOAST", toastId })
  }, [])

  const nextToast = React.useCallback(() => {
    dispatch({ type: "NEXT_TOAST" })
  }, [])

  const previousToast = React.useCallback(() => {
    dispatch({ type: "PREVIOUS_TOAST" })
  }, [])

  return {
    ...state,
    toast,
    dismiss: dismissToast,
    nextToast,
    previousToast,
    pauseAutoAdvance: () =>
      dispatch({
        type: "UPDATE_TOAST",
        toast: { id: state.toasts[state.currentIndex]?.id },
      }),
    resumeAutoAdvance: () =>
      dispatch({
        type: "UPDATE_TOAST",
        toast: { id: state.toasts[state.currentIndex]?.id },
      }),
  }
}

export { useToast, toast }
